import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Badge, Spinner, Button, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API } from '../../config/api';
import {
  faArrowLeft,
  faAnglesLeft,
  faAnglesRight
} from '@fortawesome/free-solid-svg-icons';

// Read-only FULL DETAILS board: columns = production phases, cards = tokens.
const PHASE_ORDER = [
  'unwinding',
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat',
  'coiling',
  'rewinding'
];
const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const PHASE_COLOR = {
  unwinding: '#6366f1',
  pre_stranding: '#8b5cf6',
  conductor_isolation: '#0ea5e9',
  rope: '#14b8a6',
  interior_sheat: '#f59e0b',
  armouring: '#ef4444',
  exterior_sheat: '#22c55e',
  coiling: '#ec4899',
  rewinding: '#a855f7'
};
// distinct colours per JOB so a job can be traced across every phase column
const JOB_COLORS = [
  '#0d6efd',
  '#22c55e',
  '#f59e0b',
  '#ec4899',
  '#14b8a6',
  '#a855f7',
  '#ef4444',
  '#0ea5e9',
  '#84cc16',
  '#f97316'
];
const num0 = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Number(n || 0).toLocaleString();
const U = v => String(v ?? '').toUpperCase();

const statusVariant = s => {
  const u = U(s);
  if (['DONE', 'COMPLETED', 'FINISHED'].includes(u)) return 'success';
  if (['ACTIVE', 'RUNNING', 'CONSUMING'].includes(u)) return 'primary';
  if (['PENDING', 'PLANNED'].includes(u)) return 'warning';
  return 'secondary';
};

// group a column's tokens by job, preserving first-seen job order
const groupTokensByJob = toks => {
  const order = [];
  const groups = {};
  for (const t of toks) {
    const k = t.jobId || '_';
    if (!groups[k]) {
      groups[k] = [];
      order.push(k);
    }
    groups[k].push(t);
  }
  return order.map(k => [k, groups[k]]);
};

const OrderReservationBoard = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const toggle = ph => setCollapsed(c => ({ ...c, [ph]: !c[ph] }));

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['order-full-details', orderId],
    queryFn: () =>
      axios
        .get(`${API.REDIS}/api/redis/orders/${orderId}/full-details`)
        .then(r => r.data),
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
    staleTime: 3000
  });

  const order = data?.order;
  const jobs = data?.jobs || [];
  const stock = data?.stock || { meters: 0, serials: [] };

  // per-delivery-item reservation view (needed / reserved / produced / free +
  // per-job breakdown) — reuses the sales reservations endpoint, filtered to this order.
  const { data: resvOrders } = useQuery({
    queryKey: ['order-reservations-for', orderId],
    queryFn: () =>
      axios
        .get(`${API.REDIS}/api/redis/orders/reservations`)
        .then(r => r.data?.orders || []),
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
    staleTime: 3000
  });
  const resvItems = useMemo(() => {
    const o = (resvOrders || []).find(x => String(x.orderId) === String(orderId));
    return o?.items || [];
  }, [resvOrders, orderId]);
  const resvTotals = useMemo(
    () =>
      resvItems.reduce(
        (a, it) => ({
          needed: a.needed + num0(it.qtyPlanned),
          reserved: a.reserved + num0(it.reserved),
          produced: a.produced + num0(it.produced),
          stock: a.stock + num0(it.stockReserved),
          free: a.free + num0(it.free)
        }),
        { needed: 0, reserved: 0, produced: 0, stock: 0, free: 0 }
      ),
    [resvItems]
  );

  // stable colour per job (by index) — used for card borders + legend
  const jobColor = useMemo(() => {
    const m = {};
    jobs.forEach((j, i) => {
      m[j.jobId] = JOB_COLORS[i % JOB_COLORS.length];
    });
    return m;
  }, [jobs]);

  const byPhase = useMemo(() => {
    const m = {};
    for (const j of jobs)
      for (const t of j.tokens || []) {
        const ph = t.phase || 'other';
        (m[ph] = m[ph] || []).push({
          ...t,
          jobId: j.jobId,
          jobCode: j.jobCode,
          jobName: j.name,
          productLabel: j.productLabel
        });
      }
    return m;
  }, [jobs]);

  // columns = phases in ANY of the order's job recipes (so empty phases still show)
  const phases = useMemo(() => {
    const present = new Set(Object.keys(byPhase));
    for (const j of jobs) for (const p of j.phasePlan || []) present.add(p);
    const ordered = PHASE_ORDER.filter(p => present.has(p));
    for (const p of present) if (!PHASE_ORDER.includes(p)) ordered.push(p);
    return ordered;
  }, [byPhase, jobs]);

  const phaseProgress = ph => {
    const toks = byPhase[ph] || [];
    const planned = toks.reduce((s, t) => s + num0(t.plannedLength), 0);
    const produced = toks.reduce((s, t) => s + num0(t.producedLength), 0);
    return {
      planned,
      produced,
      pct: planned > 0 ? Math.min(100, Math.round((produced / planned) * 100)) : 0
    };
  };

  // order-level stats for the KPI row
  const stats = useMemo(() => {
    let tokens = 0,
      plannedM = 0,
      producedM = 0,
      done = 0,
      active = 0,
      pending = 0;
    for (const j of jobs)
      for (const t of j.tokens || []) {
        tokens++;
        plannedM += num0(t.plannedLength);
        producedM += num0(t.producedLength);
        const u = U(t.status);
        if (['DONE', 'COMPLETED', 'FINISHED'].includes(u)) done++;
        else if (['ACTIVE', 'RUNNING', 'CONSUMING'].includes(u)) active++;
        else pending++;
      }
    return {
      jobs: jobs.length,
      tokens,
      plannedM,
      producedM,
      pct: plannedM > 0 ? Math.round((producedM / plannedM) * 100) : 0,
      done,
      active,
      pending
    };
  }, [jobs]);

  // per-job summary strip
  const jobRows = useMemo(
    () =>
      jobs.map(j => {
        const toks = j.tokens || [];
        const planned = toks.reduce((s, t) => s + num0(t.plannedLength), 0);
        const produced = toks.reduce((s, t) => s + num0(t.producedLength), 0);
        return {
          ...j,
          tokenCount: toks.length,
          planned,
          produced,
          pct: planned > 0 ? Math.min(100, Math.round((produced / planned) * 100)) : 0
        };
      }),
    [jobs]
  );

  return (
    <div className="p-3">
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => navigate('/apps/sales-management/reservations')}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
        <div>
          <h4 className="mb-0">
            Full details{order?.orderCode ? ` · ${order.orderCode}` : ''}
          </h4>
          <div className="text-muted fs-9 d-flex align-items-center gap-2 flex-wrap">
            {order?.client && (
              <Badge bg="info" variant="phoenix">
                {U(order.client)}
              </Badge>
            )}
            {order?.dueDate && <span>Due {String(order.dueDate).slice(0, 10)}</span>}
            <span>· {jobs.length} jobs</span>
          </div>
        </div>
        <span className="fs-10 text-body-tertiary ms-auto d-flex align-items-center gap-1">
          <span
            className="rounded-circle"
            style={{
              width: 8,
              height: 8,
              background: isFetching ? '#22c55e' : '#94a3b8',
              display: 'inline-block'
            }}
          />
          Live · 5s
        </span>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : isError ? (
        <div className="text-danger py-3">Failed to load full details.</div>
      ) : (
        <>
          {/* An order filled off the shelf never reaches a machine, so this
              board is legitimately empty for it. Said out loud, because a blank
              board and "nothing has been launched yet" look the same. */}
          {stock.meters > 0 && (
            <Alert variant="info" className="py-2 fs-9">
              <strong>{fmt(stock.meters)} m reserved from stock.</strong>{' '}
              {jobs.length === 0
                ? 'This order is covered from the warehouse — no production job serves it, so there is nothing to show below.'
                : 'Part of this order comes off the shelf and does not appear on the board below.'}
              {stock.serials.length > 0 && (
                <span className="ms-1 text-body-tertiary">
                  ({stock.serials.join(', ')})
                </span>
              )}
            </Alert>
          )}

          {/* KPI tiles */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {[
              ['Jobs', stats.jobs, '#6366f1'],
              ['Tokens', stats.tokens, '#0ea5e9'],
              ['Produced', `${fmt(stats.producedM)} m`, '#22c55e'],
              ['Planned', `${fmt(stats.plannedM)} m`, '#f59e0b'],
              ['Completion', `${stats.pct}%`, '#14b8a6']
            ].map(([l, v, c]) => (
              <Card
                key={l}
                className="border-0 shadow-sm"
                style={{ minWidth: 128, borderTop: `3px solid ${c}` }}
              >
                <Card.Body className="py-2 px-3">
                  <div className="text-muted fs-10">{l}</div>
                  <div className="fw-bold fs-6" style={{ color: c }}>
                    {v}
                  </div>
                </Card.Body>
              </Card>
            ))}
            <Card className="border-0 shadow-sm" style={{ minWidth: 190 }}>
              <Card.Body className="py-2 px-3">
                <div className="text-muted fs-10">Tokens by status</div>
                <div className="d-flex gap-1 mt-1 flex-wrap">
                  <Badge bg="success" variant="phoenix">
                    {stats.done} done
                  </Badge>
                  <Badge bg="primary" variant="phoenix">
                    {stats.active} active
                  </Badge>
                  <Badge bg="warning" variant="phoenix">
                    {stats.pending} pending
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* delivery plan — what this order must deliver vs what's reserved /
              produced / still free, per drum/coil item, broken down by job */}
          {resvItems.length > 0 && (
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <span className="fw-semibold fs-9">Delivery &amp; Reservations</span>
                  <div className="d-flex gap-2 fs-10">
                    <Badge bg="secondary" variant="phoenix">
                      Needed {fmt(resvTotals.needed)}
                    </Badge>
                    <Badge bg="primary" variant="phoenix">
                      Reserved {fmt(resvTotals.reserved)}
                    </Badge>
                    {resvTotals.stock > 0 && (
                      <Badge bg="info" variant="phoenix">
                        From stock {fmt(resvTotals.stock)}
                      </Badge>
                    )}
                    <Badge bg="success" variant="phoenix">
                      Produced {fmt(resvTotals.produced)}
                    </Badge>
                    {resvTotals.free > 0 && (
                      <Badge bg="warning" variant="phoenix">
                        Free {fmt(resvTotals.free)}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm align-middle fs-10 mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th>Delivery item</th>
                        <th className="text-end">Needed</th>
                        <th className="text-end">Reserved</th>
                        <th className="text-end">Produced</th>
                        <th className="text-end">Free</th>
                        <th style={{ minWidth: 150 }}>Reserved / Produced</th>
                        <th>By job</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resvItems.map(it => {
                        const q = num0(it.qtyPlanned);
                        const pctR = q > 0 ? Math.min(100, (it.reserved / q) * 100) : 0;
                        const pctP = q > 0 ? Math.min(100, (it.produced / q) * 100) : 0;
                        const spec =
                          U(it.kind) === 'DRUM'
                            ? `${it.drumMaterial || ''} ${
                                it.drumDiameter ? 'Ø' + it.drumDiameter : ''
                              }`.trim() || 'Drum'
                            : 'Coil';
                        return (
                          <tr key={it.itemId}>
                            <td className="text-nowrap fw-semibold">
                              {U(it.kind) === 'DRUM' ? '🛢 ' : '🎗 '}
                              {spec} · {fmt(it.lengthPerUnit)}m
                            </td>
                            <td className="text-end fw-semibold">{fmt(q)}</td>
                            <td className="text-end" style={{ color: '#6366f1' }}>
                              {fmt(it.reserved)}
                            </td>
                            <td className="text-end text-success">
                              {fmt(it.produced)}
                            </td>
                            <td className="text-end">
                              {it.free > 0 ? (
                                <span className="text-warning fw-semibold">
                                  {fmt(it.free)}
                                </span>
                              ) : (
                                '0'
                              )}
                            </td>
                            <td>
                              <ProgressBar style={{ height: 8, minWidth: 110 }}>
                                <ProgressBar variant="success" now={pctP} key="p" />
                                <ProgressBar
                                  variant="primary"
                                  now={Math.max(0, pctR - pctP)}
                                  key="r"
                                />
                              </ProgressBar>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {(it.byJob || []).length === 0 ? (
                                  <span className="text-body-tertiary">—</span>
                                ) : (
                                  it.byJob.map(bj => (
                                    <Badge
                                      key={bj.jobId}
                                      bg="secondary"
                                      variant="phoenix"
                                      className="fs-10"
                                      style={{
                                        borderLeft: `3px solid ${
                                          jobColor[bj.jobId] || '#888'
                                        }`
                                      }}
                                      title={bj.name || bj.jobCode || ''}
                                    >
                                      {bj.jobCode || (bj.jobId || '').slice(-6)}:{' '}
                                      {bj.reserved}r
                                      {bj.produced ? ` · ${bj.produced}p` : ''}
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* overall production progress */}
          <div className="mb-3">
            <div className="d-flex justify-content-between fs-10 text-muted mb-1">
              <span>Overall production</span>
              <span>
                {fmt(stats.producedM)} / {fmt(stats.plannedM)} m · {stats.pct}%
              </span>
            </div>
            <ProgressBar now={stats.pct} variant="success" style={{ height: 10 }} />
          </div>

          {/* per-job summary strip */}
          {jobRows.length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold fs-9 mb-2">Jobs ({jobRows.length})</div>
              <div className="d-flex flex-wrap gap-2">
                {jobRows.map(j => (
                  <Card
                    key={j.jobId}
                    className="border shadow-sm"
                    style={{
                      minWidth: 220,
                      flex: '1 1 220px',
                      maxWidth: 320,
                      borderTop: `3px solid ${jobColor[j.jobId] || '#888'}`
                    }}
                  >
                    <Card.Body className="p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold fs-10 d-flex align-items-center gap-1">
                          <span
                            style={{
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              background: jobColor[j.jobId] || '#888',
                              display: 'inline-block'
                            }}
                          />
                          {j.jobCode || (j.jobId || '').slice(-6)}
                        </span>
                        <Badge bg="secondary" variant="phoenix" className="fs-10">
                          {j.tokenCount} tok
                        </Badge>
                      </div>
                      <div
                        className="fs-10 text-muted text-truncate"
                        title={j.productLabel}
                      >
                        {j.productLabel || j.name || '—'}
                      </div>
                      <ProgressBar
                        now={j.pct}
                        variant="success"
                        style={{ height: 5 }}
                        className="my-1"
                      />
                      <div className="d-flex justify-content-between fs-10 text-body-tertiary">
                        <span>
                          {fmt(j.produced)}/{fmt(j.planned)} m
                        </span>
                        <span>{fmt(j.allocatedMeters)} m order</span>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* phase board */}
          {phases.length === 0 ? (
            <div className="text-muted py-4 text-center">
              No production planned yet.
            </div>
          ) : (
            <>
              <div className="fw-semibold fs-9 mb-2">Production phases</div>
              <div
                className="d-flex gap-3 overflow-auto pb-3 scrollbar"
                style={{ minHeight: 440 }}
              >
          {phases.map(ph => {
            const color = PHASE_COLOR[ph] || '#6366f1';
            const toks = byPhase[ph] || [];
            const prog = phaseProgress(ph);

            if (collapsed[ph]) {
              return (
                <div
                  key={ph}
                  onClick={() => toggle(ph)}
                  className="rounded border d-flex flex-column align-items-center py-3 bg-body-tertiary"
                  style={{
                    minWidth: 46,
                    maxWidth: 46,
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    borderTop: `3px solid ${color}`
                  }}
                  title="Expand"
                >
                  <FontAwesomeIcon icon={faAnglesRight} style={{ color }} />
                  <div
                    className="mt-3 fw-semibold text-nowrap"
                    style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontSize: 12
                    }}
                  >
                    {PHASE_LABEL[ph] || ph}
                  </div>
                  <Badge bg="secondary" variant="phoenix" className="mt-3">
                    {toks.length}
                  </Badge>
                </div>
              );
            }

            return (
              <div
                key={ph}
                className="rounded border bg-body-tertiary d-flex flex-column"
                style={{
                  minWidth: 256,
                  maxWidth: 360,
                  flex: '1 1 256px',
                  borderTop: `3px solid ${color}`
                }}
              >
                <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom border-translucent">
                  <span className="fw-bold" style={{ color }}>
                    {PHASE_LABEL[ph] || ph}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <Badge style={{ background: color, color: '#fff' }}>
                      {toks.length}
                    </Badge>
                    <Button
                      variant="link"
                      className="p-0 text-body-tertiary"
                      onClick={() => toggle(ph)}
                      title="Collapse"
                    >
                      <FontAwesomeIcon icon={faAnglesLeft} />
                    </Button>
                  </div>
                </div>

                <div className="px-3 pt-2">
                  <ProgressBar
                    now={prog.pct}
                    style={{ height: 6, backgroundColor: 'rgba(128,128,128,0.15)' }}
                  >
                    <ProgressBar now={prog.pct} style={{ backgroundColor: color }} />
                  </ProgressBar>
                  <div className="fs-10 text-muted mt-1">
                    {fmt(prog.produced)} / {fmt(prog.planned)} m produced
                  </div>
                </div>

                <div
                  className="d-flex flex-column gap-2 p-2"
                  style={{ maxHeight: 540, overflowY: 'auto' }}
                >
                  {toks.length === 0 ? (
                    <div
                      className="text-center text-body-tertiary fs-10 py-4 rounded"
                      style={{ border: '1px dashed rgba(128,128,128,0.4)' }}
                    >
                      Not programmed
                    </div>
                  ) : (
                    groupTokensByJob(toks).map(([jid, jtoks]) => (
                      <div key={jid} className="mb-1">
                        <div className="d-flex align-items-center gap-1 mb-1">
                          <span
                            style={{
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              background: jobColor[jid] || '#888',
                              display: 'inline-block'
                            }}
                          />
                          <span className="fs-10 fw-semibold text-truncate">
                            {jtoks[0].jobCode || (jid || '').slice(-6)}
                          </span>
                          <Badge
                            bg="secondary"
                            variant="phoenix"
                            className="fs-10 ms-auto"
                          >
                            {jtoks.length}
                          </Badge>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {jtoks.map((t, i) => {
                            const sv = statusVariant(t.status);
                            // FINAL token for THIS order = final-phase drum/coil tied to
                            // an order delivery item → gold outline.
                            const isFinal =
                        ['exterior_sheat', 'coiling', 'rewinding'].includes(ph) &&
                        !!t.orderItemId;
                      const tPct =
                        t.plannedLength > 0
                          ? Math.min(
                              100,
                              Math.round((t.producedLength / t.plannedLength) * 100)
                            )
                          : t.status && U(t.status) === 'DONE'
                            ? 100
                            : 0;
                      return (
                        <Card
                          key={t.tokenId || i}
                          className="shadow-sm"
                          style={{
                            border: isFinal
                              ? '2px solid #eab308'
                              : '1px solid rgba(128,128,128,0.2)',
                            borderLeft: `5px solid ${jobColor[t.jobId] || color}`,
                            boxShadow: isFinal
                              ? '0 0 0 2px rgba(234,179,8,0.2)'
                              : undefined
                          }}
                        >
                          <Card.Body className="p-2">
                            <div className="d-flex justify-content-between align-items-center mb-1 gap-1">
                              <span
                                className="fw-semibold fs-10 text-truncate"
                                title={t.productLabel || t.jobName}
                              >
                                {t.productLabel || t.jobName || '—'}
                              </span>
                              <Badge bg={sv} variant="phoenix" className="fs-10">
                                {U(t.status) || '—'}
                              </Badge>
                            </div>
                            <div className="fs-10 text-muted mb-1">
                              {t.drumDiameter
                                ? `${t.drumMaterial || ''} Ø${t.drumDiameter} · `
                                : ''}
                              {t.coilCount ? `${t.coilCount} coils · ` : ''}
                              {fmt(t.producedLength || t.plannedLength)}m
                            </div>
                            <ProgressBar
                              now={tPct}
                              variant={sv}
                              style={{ height: 4 }}
                            />
                            <div className="d-flex justify-content-between align-items-center fs-10 text-body-tertiary mt-1">
                              <span>{t.jobCode || ''}</span>
                              <span>{t.machineId ? U(t.machineId) : ''}</span>
                            </div>
                            <div className="d-flex gap-1 mt-1 flex-wrap">
                              <Badge
                                bg={t.orderItemId ? 'primary' : 'secondary'}
                                variant="phoenix"
                                className="fs-10"
                              >
                                {t.orderItemId ? 'order' : 'stock'}
                              </Badge>
                              {isFinal && (
                                <Badge
                                  className="fs-10"
                                  style={{ background: '#eab308', color: '#000' }}
                                >
                                  ★ FINAL
                                </Badge>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default OrderReservationBoard;
